import React, { useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import LabsAppBar, { ActionButton } from '@components/LabsAppBar';
import TableComponent from '@components/shared/TableComponent';
import { ColumnDef } from '@tanstack/react-table';
import { Trash2, Download, FileJson } from 'lucide-react';
import { fetchAllUnifiedSchemas, deleteUnifiedSchema, updateUnifiedSchema } from '@/services/schemaDefinitionApiService';
import { UnifiedSchema, SchemaType } from '@/types/schema';
import { Integration } from '@/types/integration'; // Import Integration type
import ConfirmDialog from '@components/dialogs/ConfirmDialog';
import RenameSchemaDialog from '@components/dialogs/RenameSchemaDialog';
import { useNotifications } from '@/contexts/NotificationContext';
import { buildParameterTree, toFriendlyName } from '@/utils/schemaUtils';
import { sentenceCase } from 'change-case';
import { integrationService } from '@/services/integration.service'; // Import integrationService
import { Link } from 'react-router-dom';

interface SchemaListPageProps {
  schemaType: SchemaType;
}

const getSchemaTypeIcon = (type: SchemaType | string): string => {
  switch (type) {
    case 'vendor_schema':
      return 'Package';
    case 'customer_schema':
      return 'Globe';
    case 'integration_schema':
      return 'Box';
    default:
      return 'FileChartColumn';
  }
};

const getSchemaTypeDisplayName = (type: SchemaType | string): string => {
  if (type === 'customer_schema') {
    return 'Customer Schemas';
  }
  const friendlyName = toFriendlyName(type || '').replace(' Schema', '');
  return `${friendlyName} Schemas`;
};

const SchemaListPage: React.FC<SchemaListPageProps> = ({ schemaType }) => {
  const navigate = useNavigate();
  const { showNotification } = useNotifications();
  const [translationSchemas, setTranslationSchemas] = React.useState<UnifiedSchema[]>([]);
  const [integrations, setIntegrations] = React.useState<Integration[]>([]); // New state for integrations
  const [isLoading, setIsLoading] = React.useState(true);
  const [showConfirmDeleteDialog, setShowConfirmDeleteDialog] = React.useState(false);
  const [schemaToDelete, setSchemaToDelete] = React.useState<UnifiedSchema | null>(null);

  const [showRenameDialog, setShowRenameDialog] = React.useState(false);
  const [schemaToRename, setSchemaToRename] = React.useState<UnifiedSchema | null>(null);

  const location = useLocation();
  React.useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);
      const [schemasData, integrationsResponse] = await Promise.all([
        fetchAllUnifiedSchemas(schemaType),
        integrationService.getIntegrations(), // Fetch integrations
      ]);
      setTranslationSchemas(schemasData);
      setIntegrations(integrationsResponse.data); // Set integrations from the 'data' property
      setIsLoading(false);
    };
    loadData();
  }, [location.key, location, schemaType]);

  const removeInternalIds = (obj: any): any => {
    if (Array.isArray(obj)) {
      return obj.map(item => removeInternalIds(item));
    }
    if (typeof obj === 'object' && obj !== null) {
      const newObj: any = {};
      for (const key in obj) {
        if (key !== 'id' && key !== 'uuid') {
          newObj[key] = removeInternalIds(obj[key]);
        }
      }
      return newObj;
    }
    return obj;
  };

  const handleExportSchema = (schema: UnifiedSchema) => {
    // Create a deep copy and remove internal IDs
    const exportedSchema = removeInternalIds(schema);

    // Reconstruct the parameter tree for export
    exportedSchema.parameters = buildParameterTree(exportedSchema.parameters);

    const fileName = `${schema.name || 'schema'}.json`;
    const jsonStr = JSON.stringify(exportedSchema, null, 2);
    const blob = new Blob([jsonStr], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    showNotification('success', 'Schema exported successfully');
  };

  const handleRenameSchema = React.useCallback(async (newName: string, newDescription: string) => {
    if (!schemaToRename) return;

    try {
      const updatedSchema: UnifiedSchema = {
        ...schemaToRename,
        name: newName,
        description: newDescription,
      };
      const result = await updateUnifiedSchema(updatedSchema);
      if (result) {
        setTranslationSchemas(prev =>
          prev.map(schema => (schema.id === result.id ? result : schema))
        );
        showNotification('success', 'Schema renamed successfully');
        setShowRenameDialog(false);
        setSchemaToRename(null);
      } else {
        showNotification('error', 'Failed to rename schema.');
      }
    } catch (error) {
      console.error('Error renaming schema:', error);
      showNotification('error', 'Error renaming schema.');
    }
  }, [schemaToRename, showNotification, setTranslationSchemas]);

  const handleTranslationSchemaRowClick = (row: UnifiedSchema) => {
    navigate(`/labs/${schemaType}/edit/${row.id}`, { state: { sourceName: row.name, forceLoadFromBackend: true } });
  };

  const handleAddTranslationSchema = () => {
    navigate(`/labs/${schemaType}/edit/new`, { state: { newSchema: true, schemaType } });
  };

  const handleDeleteTranslationSchema = (schema: UnifiedSchema) => {
    setSchemaToDelete(schema);
    setShowConfirmDeleteDialog(true);
  };

  const confirmDeleteSchema = async () => {
    if (schemaToDelete) {
      try {
        const success = await deleteUnifiedSchema(schemaToDelete.id);
        if (success) {
          setTranslationSchemas(prev => prev.filter(schema => schema.id !== schemaToDelete.id));
          showNotification('success', `Schema "${schemaToDelete.name}" deleted successfully`);
        } else {
          showNotification('error', `Failed to delete schema "${schemaToDelete.name}".`);
        }
      } catch (error) {
        console.error('Error deleting schema:', error);
        showNotification('error', `Error deleting schema "${schemaToDelete.name}".`);
      } finally {
        setSchemaToDelete(null);
        setShowConfirmDeleteDialog(false);
      }
    }
  };

  const cancelDeleteSchema = () => {
    setSchemaToDelete(null);
    setShowConfirmDeleteDialog(false);
  };

  const updatedTranslationColumns: ColumnDef<UnifiedSchema>[] = useMemo(() => [
    {
      accessorKey: 'name',
      header: 'Name',
      cell: ({ row }) => {
        const integration = row.original.integrationId
          ? integrations.find(int => int.id === row.original.integrationId)
          : null;
        const isCustomerOrPipelineSchema = row.original.type === 'customer_schema' || row.original.type === 'pipeline_schema';

        const toPath = `/labs/${schemaType}/edit/${row.original.id}`;
        return (
          <Link
            to={toPath}
            state={{ sourceName: row.original.name, forceLoadFromBackend: true }}
            className="flex items-center group"
            onClick={(e) => e.stopPropagation()} // Prevent TableComponent's onRowClick
          >
            <div className="w-8 h-8 flex items-center justify-center rounded-full mr-1">
              {isCustomerOrPipelineSchema ? (
                <FileJson className="h-5 w-5 text-gray-500" />
              ) : integration ? (
                <img src={integration.logo_url} alt={integration.name} className="h-5 w-5 rounded-full" />
              ) : null}
            </div>
            <span className="font-medium text-gray-900 dark:text-white group-hover:text-blue-600 dark:group-hover:text-blue-300 transition-colors duration-200">
              {row.original.name}
            </span>
          </Link>
        );
      },
    },
    {
      accessorKey: 'description',
      header: 'Description',
      cell: ({ getValue }) => getValue(),
    },
    {
      accessorKey: 'events',
      header: 'No. of Events',
      cell: ({ row }) => (row.original.events || []).length,
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ getValue }) => {
        const status = getValue() as 'draft' | 'published';
        const colorClass = status === 'draft' ? 'text-yellow-800 bg-yellow-100 dark:bg-yellow-900 dark:text-yellow-200' : 'text-green-800 bg-green-100 dark:bg-green-900 dark:text-green-200';
        return (
          <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${colorClass}`}>
            {status.charAt(0).toUpperCase() + status.slice(1)}
          </span>
        );
      },
    },
    {
      id: 'actions',
      header: '',
      cell: ({ row }) => (
        <div className="flex justify-end space-x-2">
          <button
            className="text-gray-400 hover:text-blue-600 dark:text-gray-500 dark:hover:text-blue-300 transition-colors duration-200"
            onClick={(e) => {
              e.stopPropagation();
              handleExportSchema(row.original);
            }}
          >
            <Download className="h-5 w-5" />
          </button>
          <button
            className="text-gray-400 hover:text-red-600 dark:text-gray-500 dark:hover:text-red-300 transition-colors duration-200"
            onClick={(e) => {
              e.stopPropagation();
              handleDeleteTranslationSchema(row.original);
            }}
          >
            <Trash2 className="h-5 w-5" />
          </button>
        </div>
      ),
    },
  ], [handleDeleteTranslationSchema, handleExportSchema, integrations]);

  const schemaTitle = useMemo(() => {
    if (schemaType === 'customer_schema') {
      return 'Raw Customer Schemas';
    }
    if (!schemaType) return "Schemas";
    return `${sentenceCase(schemaType.replace('_', ' '))}s`;
  }, [schemaType]);

  const breadcrumbs = useMemo(() => {
    const typeIcon = getSchemaTypeIcon(schemaType || '');
    const typeDisplayName = getSchemaTypeDisplayName(schemaType || '');

    return [
      { name: typeDisplayName, iconName: typeIcon },
    ];
  }, [schemaType]);

  const newSchemaButton: ActionButton = { // Explicitly type as ActionButton
    label: 'New Schema',
    onClick: handleAddTranslationSchema,
    iconName: 'PlusCircle',
    variant: 'primary',
  };

  return (
    <div className="flex flex-col h-full">
      <LabsAppBar breadcrumbs={breadcrumbs} actions={[newSchemaButton]} />
      <div className="flex-1 overflow-y-auto p-4 sm:p-6 lg:p-8">
        <div className="space-y-flex flex-col gap-10 max-w-9xl mx-auto">
            <TableComponent
              title={schemaTitle}
              description={
                schemaType === 'customer_schema'
                  ? 'Define the structure of incoming event data to unlock better informed transform decisions.'
                  : schemaType === 'pipeline_schema'
                  ? 'Define the structure of Pipeline Transforms output event data for full data control.'
                  : schemaType === 'vendor_schema'
                  ? 'Define the precise data format each vendor requires to ensure compliance and prevent delivery errors.'
                  : 'Define and enforce the structure of incoming event data for data quality and consistency.' // Default description
              }
              data={translationSchemas}
              columns={updatedTranslationColumns}
              onRowClick={handleTranslationSchemaRowClick}
              isLoading={isLoading}
              isError={false}
              buttons={<></>} // Provide an empty fragment as the buttons prop is required
            />

      {showConfirmDeleteDialog && schemaToDelete && (
        <ConfirmDialog
          onClose={cancelDeleteSchema}
          onConfirm={confirmDeleteSchema}
          title="Confirm Schema Deletion"
          message={`Are you sure you want to delete the schema "${schemaToDelete.name}"? This action cannot be undone.`}
          confirmText="Delete"
          isDanger={true}
          z-index="120"
        />
      )}

      {showRenameDialog && schemaToRename && (
        <RenameSchemaDialog
          open={showRenameDialog}
          onClose={() => setShowRenameDialog(false)}
          currentSchemaName={schemaToRename.name}
          currentSchemaDescription={schemaToRename.description}
          onRenameConfirm={handleRenameSchema}
        />
      )}
        </div>
      </div>
    </div>
  );
};

export default SchemaListPage;
